import React, { forwardRef, memo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { IErrMsg } from './types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DescriptionBtnWithPopup } from '../buttons/PromptBtn/DescriptionBtnWithPopup'
import { RequiredMark } from '../ui/RequiredMark/RequiredMark'
import { RiskyParamUnlockBtn } from './components/RiskyParam/RiskyParamUnlockBtn'

export interface PasswordInputProps {
    className?: string
    state: any
    touched?: any
    errors?: any
    errorText?: string
    name: string
    label?: string
    placeholder?: string
    changeWarning?: string
    isDisabled?: boolean
    tooltipText?: string
    isColumn?: boolean
    isLabelHidden?: boolean
    validationSchema?: any
    inputType?: string
    isRequired?: boolean
    isFeedbackFixed?: boolean
    errMsg?: IErrMsg

    setState(state: any): any
    setTouched?(touched: any): any
    onEnter?: Function
    onBlur?(): void
    onChange?(value: any): void
}

export const PasswordInput = memo(
    forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
        {
            state,
            setState,
            touched,
            setTouched,
            errors,
            errMsg,
            name,
            className,
            placeholder,
            tooltipText,
            changeWarning,
            label,
            isDisabled,
            isColumn,
            isLabelHidden,
            isRequired,
            inputType,
            isFeedbackFixed,
            onEnter,
            onBlur,
            onChange,
        },
        ref
    ) {
        const [type, setType] = useState('password')
        const [isChanged, setIsChanged] = useState(false)
        const [isRiskyParam, setIsRiskyParam] = useState(!!changeWarning)

        const handleUnlockRiskyParam = () => setIsRiskyParam(false)

        const showPassword = (): void => {
            setType('string')
        }

        const hidePassword = (): void => {
            setType('password')
        }

        const handleChange = (event: any): void => {
            const newValue = event.target.value
            setTouched &&
                setTouched({
                    ...touched,
                    [name]: true,
                })
            setState({
                ...state,
                [name]: newValue,
            })
            if (typeof onChange === 'function') onChange(newValue)
            setIsChanged(true)
        }

        const handleBlur = (): void => {
            if (typeof onBlur === 'function') return onBlur()
            setTouched && setTouched((prev: any) => ({ ...prev, [name]: true }))
        }

        const handleOnKeyDown = (evt: any) => {
            if (evt.key === 'Enter' && typeof onEnter === 'function') {
                onEnter(evt)
            }
        }

        const setSecretState = (): string => {
            if (inputType === 'secret') {
                return !isChanged && state?.[name] ? '******' : state?.[name]
            } else {
                return state?.[name]
            }
        }

        const dataState = setSecretState()
        const getValidationResult = (): boolean =>
            touched && touched[name] && errors && errors[name]
        const isInvalid: boolean = getValidationResult()

        return (
            <Form>
                <Form.Group
                    style={{ maxWidth: '600px' }}
                    className={`${className} ${
                        isInvalid && !isFeedbackFixed && 'mb-4'
                    } ${
                        !isLabelHidden && 'mw-250'
                    } d-flex flex-1 mt-1 mb-1 justify-content-between align-items-baseline`}
                >
                    {label && !isLabelHidden ? (
                        <Form.Label
                            style={{
                                maxWidth: isColumn ? '30%' : 'auto',
                                opacity: isRiskyParam ? '50%' : '100%',
                            }}
                            className="label-main mt-1 text-capital pe-2"
                        >
                            <span className="label-main-wrapper p-relative">
                                <FormattedMessage id={label} />:{' '}
                                {isRequired && <RequiredMark />}
                            </span>
                        </Form.Label>
                    ) : null}

                    <div
                        style={{ width: '70%' }}
                        className={`d-flex align-items-center ${
                            !isColumn ? 'flex-1' : ''
                        }`}
                    >
                        <span className="d-flex align-items-center flex-column w-100 p-relative">
                            {isRiskyParam && <div className="input-overlay" />}
                            <div style={{ display: 'none' }}>
                                <input
                                    type="text"
                                    id="dummyInput"
                                    autoComplete="off"
                                />
                            </div>
                            <Form.Control
                                ref={ref}
                                className={`input-main ${
                                    !tooltipText
                                        ? 'input-padding-sm'
                                        : 'password-input-padding'
                                }`}
                                autoComplete="off"
                                type={type}
                                placeholder={placeholder}
                                value={dataState}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onKeyDown={handleOnKeyDown}
                                disabled={!!isDisabled}
                                isInvalid={isInvalid}
                                tabIndex={isRiskyParam ? -1 : 0}
                            />
                            <Form.Control.Feedback
                                className="input-feedback"
                                type="invalid"
                            >
                                <FormattedMessage
                                    id={errMsg?.message || 'password-error'}
                                    values={errMsg?.values}
                                    tagName="span"
                                />
                            </Form.Control.Feedback>
                            <div className="d-flex gap-2 icon-absolute">
                                <div
                                    onMouseDown={showPassword}
                                    onMouseLeave={hidePassword}
                                    onMouseUp={hidePassword}
                                >
                                    <FontAwesomeIcon
                                        className="opacity-50"
                                        icon={
                                            type === 'password'
                                                ? faEyeSlash
                                                : faEye
                                        }
                                    />
                                </div>
                                {tooltipText && (
                                    <DescriptionBtnWithPopup
                                        type={'default'}
                                        header={label ?? ''}
                                        description={tooltipText ?? ''}
                                    />
                                )}
                            </div>
                        </span>
                        {isRiskyParam && (
                            <RiskyParamUnlockBtn
                                onUnlockConfirm={handleUnlockRiskyParam}
                                changeWarning={changeWarning}
                            />
                        )}
                    </div>
                </Form.Group>
            </Form>
        )
    })
)
